import ExtractionDataIcon from '~/common/componentMapper/QuestionExtraction/components/ExtractionDataIconComponent';
import Flex from '~/common/components/Flex';
import { Tbody, Th, Tr } from '~/common/components/Tables';
import Text from '~/common/components/Texts/Text';

import CheckboxController from '../controllers/CheckboxController';
import InputController from '../controllers/InputController';
import { useQuestionLibraryActions } from '../store/questionLibraryStore';
import { SMART_ANSWER_LABEL_TYPES } from '../utils/constants';
import { useSelectSmartAnswer } from '../utils/hooks';
import { useConditionsEvaluator } from '../utils/useConditionsEvaluator';
import SnapShotButton from './smartQuestionComponents/SnapshotButton';
import {
  StyledTHead,
  StyledTable,
  StyledTd,
} from './styledComponents/styledTableComponents';
import ConditionEvaluator from './utilityComponents/ConditionEvaluator';
import ProcessedText from './utilityComponents/ProcessedText';
import QuestionResolutionWrapper from './utilityComponents/QuestionResolutionWrapper';

export default function QuestionLibraryTable({ component }) {
  const {
    name,
    columns,
    conditions,
    rows,
    helperText = '',
    hasBottomBorder = false,
    resolutions = [],
  } = component;

  const { isVisible } = useConditionsEvaluator(conditions);
  return (
    <ConditionEvaluator conditionResult={isVisible} conditions={conditions}>
      <QuestionResolutionWrapper
        resolutions={resolutions}
        hasBottomBorder={hasBottomBorder}
        isVisible={conditions}
      >
        {helperText && (
          <Text data-testid={`${name}-helperText`} as="strong">
            {helperText}
          </Text>
        )}
        <StyledTable id={name}>
          <StyledTHead>
            <Tr>
              {columns.map((column) => (
                <Th
                  key={column.id}
                  width={column.width ?? `${100 / columns.length}%`}
                  scope="col"
                >
                  {column.header}
                </Th>
              ))}
            </Tr>
          </StyledTHead>

          <Tbody>
            {rows.map((row) => (
              <ConditionEvaluator conditions={row.conditions} key={row.name}>
                <Tr>
                  {row.cells.map((cell, i) => (
                    <QuestionBuilderCell
                      key={cell.id}
                      cell={cell}
                      width={columns[i].width ?? `${100 / columns.length}%`}
                    />
                  ))}
                </Tr>
              </ConditionEvaluator>
            ))}
          </Tbody>
        </StyledTable>
      </QuestionResolutionWrapper>
    </ConditionEvaluator>
  );
}

function QuestionBuilderCell({ cell, width }) {
  return (
    <StyledTd width={width}>
      {cell.cellType === 'question' && (
        <QuestionBuilderQuestionCell question={cell} />
      )}
      {cell.cellType === 'text' && (
        <ProcessedText
          text={cell.text}
          placeholders={cell.placeholders ?? []}
          as={'strong'}
        />
      )}
    </StyledTd>
  );
}

function QuestionBuilderQuestionCell({ question }) {
  const inputLabelId = `${question.name}-label`;
  const { setFocusedQuestion } = useQuestionLibraryActions();
  const smartAnswer = useSelectSmartAnswer(question.name);
  const { isRequired } = useConditionsEvaluator(question.conditions);
  if (question.type === 'checkbox') {
    return (
      <Flex alignContent={'center'} padding={'0.5rem 0.25rem'}>
        <CheckboxController
          question={question}
          aria-labelledby={inputLabelId}
        />
      </Flex>
    );
  }
  return (
    <Flex
      alignItems={'center'}
      padding={'0.5rem 0.25rem'}
      gap={'1rem'}
      aria-labelledby={inputLabelId}
    >
      <InputController
        question={question}
        isRequirable={isRequired}
        onFocus={() => setFocusedQuestion(question.name)}
        smartAnswerLayout={SMART_ANSWER_LABEL_TYPES.NO_SNAPSHOT_BUTTON_OR_ICON}
        maxRowWidth={question.maxRowWidth}
        aria-labelledby={inputLabelId}
      />
      <SnapShotButton
        snippets={smartAnswer?.aiResponse?.snippets}
        questionName={question.name}
        aria-label={`Show snapshot for ${question.label || question.name}`}
      />
      <ExtractionDataIcon
        iconType={smartAnswer?.iconType}
        testId={`${question.name}-extraction-icon-${smartAnswer?.iconType}`}
        aria-label={`Extraction data: ${smartAnswer?.iconType || 'none'}`}
      />
    </Flex>
  );
}
